import type { ChatMessage } from '@crowdset/contracts';
import { LIMITS } from '@crowdset/contracts/constants';
import { tokenizeChat } from '@crowdset/domain';
import { Avatar } from '@crowdset/ui';
import { ArrowDown, DotsThree, Flag, PaperPlaneRight, Trash } from '@phosphor-icons/react';
import { DropdownMenu } from 'radix-ui';
import { memo, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router';
import { errorMessage } from '../../lib/api';
import { usePrefs } from '../../lib/prefs';
import { fetchChatBefore, useSession } from '../../lib/queries';
import { useCommand } from '../../room/context';
import type { SystemLine } from '../../room/reducer';
import { useRoomStore } from '../../room/store';
import { Button, cx } from '../../ui/kit';
import { useRoomDialogs } from './dialogs-store';

type Row = { kind: 'msg'; at: number; m: ChatMessage } | { kind: 'sys'; at: number; s: SystemLine };

export function Chat() {
  const data = useRoomStore((s) => s.data)!;
  const session = useSession().data!;
  const blocked = usePrefs((p) => p.blocked);
  const [older, setOlder] = useState<ChatMessage[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const scroller = useRef<HTMLDivElement>(null);
  const [atBottom, setAtBottom] = useState(true);

  const rows = useMemo<Row[]>(() => {
    const byId = new Map<string, ChatMessage>();
    for (const m of older) byId.set(m.id, m);
    for (const m of data.chat) byId.set(m.id, m);
    const msgs: Row[] = [...byId.values()]
      .filter((m) => !blocked.includes(m.author.id))
      .map((m) => ({ kind: 'msg' as const, at: Date.parse(m.createdAt), m }));
    const sys: Row[] = data.system.map((s) => ({ kind: 'sys' as const, at: s.atMs, s }));
    return [...msgs, ...sys].sort((a, b) => a.at - b.at);
  }, [older, data.chat, data.system, blocked]);

  useLayoutEffect(() => {
    const el = scroller.current;
    if (el && atBottom) el.scrollTop = el.scrollHeight;
  }, [rows.length, atBottom]);

  const loadOlder = async () => {
    const all = [...older, ...data.chat];
    const oldest = all.reduce<ChatMessage | null>(
      (a, m) => (!a || Number(m.id) < Number(a.id) ? m : a),
      null,
    );
    if (!oldest) return;
    setLoadingOlder(true);
    const el = scroller.current;
    const prevHeight = el?.scrollHeight ?? 0;
    try {
      const r = await fetchChatBefore(data.room.slug, oldest.id);
      setOlder((o) => [...r.items, ...o]);
      setHasMore(r.hasMore);
      requestAnimationFrame(() => {
        if (el) el.scrollTop = el.scrollHeight - prevHeight;
      });
    } catch (e) {
      useRoomStore.getState().toast(errorMessage(e), 'error');
    } finally {
      setLoadingOlder(false);
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div
        ref={scroller}
        onScroll={(e) => {
          const el = e.currentTarget;
          setAtBottom(el.scrollHeight - el.scrollTop - el.clientHeight < 40);
        }}
        className="cs-scroll relative min-h-0 flex-1 overflow-y-auto px-3 py-3"
        role="log"
        aria-live="polite"
        aria-relevant="additions"
        aria-label="Room chat"
        tabIndex={0}
      >
        {hasMore && data.chat.length >= 20 && (
          <div className="mb-3 flex justify-center">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => void loadOlder()}
              loading={loadingOlder}
            >
              Load earlier messages
            </Button>
          </div>
        )}
        {rows.length === 0 && (
          <p className="px-2 py-8 text-center text-sm text-fg-subtle">
            No messages yet. Say hello to the room.
          </p>
        )}
        <ul className="flex flex-col gap-0.5">
          {rows.map((r) =>
            r.kind === 'sys' ? (
              <li
                key={`s-${r.s.id}`}
                className={cx(
                  'px-2 py-1.5 text-xs',
                  r.s.tone === 'warn' ? 'text-warn/90' : 'text-fg-subtle',
                )}
              >
                {r.s.text}
              </li>
            ) : (
              <MessageRow key={r.m.id} m={r.m} myHandle={session.user?.handle ?? null} />
            ),
          )}
        </ul>
      </div>
      {!atBottom && (
        <div className="relative">
          <button
            className="absolute -top-12 left-1/2 flex -translate-x-1/2 items-center gap-1 rounded-full border border-white/10 bg-ink-800 px-3 py-1.5 text-xs shadow-lg hover:bg-ink-700"
            onClick={() => {
              const el = scroller.current;
              if (el) el.scrollTop = el.scrollHeight;
              setAtBottom(true);
            }}
          >
            <ArrowDown size={12} weight="bold" aria-hidden /> Latest
          </button>
        </div>
      )}
      <Composer />
    </div>
  );
}

const MessageRow = memo(function MessageRow({
  m,
  myHandle,
}: {
  m: ChatMessage;
  myHandle: string | null;
}) {
  const data = useRoomStore((s) => s.data)!;
  const openDialog = useRoomDialogs((s) => s.open);
  const del = useCommand('moderation.deleteChat', { success: 'Message removed.' });
  const mentioned = !!myHandle && m.mentions.includes(myHandle);
  const time = new Date(m.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const isMod = data.me.role === 'moderator' || data.me.role === 'owner';
  const isMine = m.author.id === data.me.userId;
  const canAct = !m.deleted && data.me.userId && (isMod || !isMine);
  return (
    <li
      className={cx(
        'group relative flex gap-2.5 rounded-xl px-2 py-1.5 hover:bg-white/[0.03]',
        mentioned && 'bg-accent/[0.07] ring-1 ring-inset ring-accent/20',
      )}
    >
      <div
        className="mt-0.5 flex h-7 w-7 shrink-0 items-end justify-center overflow-hidden rounded-lg bg-ink-800"
        aria-hidden
      >
        <Avatar avatarId={m.author.avatarId} size={26} className="translate-y-2" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="truncate text-[13px] font-medium">{m.author.displayName}</span>
          <time className="shrink-0 font-mono text-[10px] text-fg-subtle" dateTime={m.createdAt}>
            {time}
          </time>
        </div>
        {m.deleted ? (
          <p className="text-[13px] italic text-fg-subtle">Message removed by a moderator.</p>
        ) : (
          <p className="break-words text-[13.5px] leading-relaxed text-fg/90">
            {tokenizeChat(m.body).map((seg, i) =>
              seg.type === 'text' ? (
                <span key={i}>{seg.text}</span>
              ) : seg.type === 'mention' ? (
                <Link
                  key={i}
                  to={`/u/${seg.handle}`}
                  className={cx(
                    'font-medium',
                    seg.handle === myHandle ? 'text-accent-strong' : 'text-accent',
                  )}
                >
                  @{seg.handle}
                </Link>
              ) : (
                <a
                  key={i}
                  href={seg.href}
                  target="_blank"
                  rel="noopener noreferrer nofollow ugc"
                  className="text-accent underline decoration-accent/40 underline-offset-2"
                >
                  {seg.text}
                </a>
              ),
            )}
          </p>
        )}
      </div>
      {canAct && (
        <DropdownMenu.Root modal={false}>
          <DropdownMenu.Trigger asChild>
            <button
              className="absolute right-1.5 top-1.5 flex h-7 w-7 items-center justify-center rounded-lg text-fg-subtle opacity-0 hover:bg-white/[0.07] hover:text-fg focus-visible:opacity-100 group-hover:opacity-100 data-[state=open]:opacity-100"
              aria-label={`Actions for message from ${m.author.displayName}`}
            >
              <DotsThree size={18} weight="bold" />
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align="end"
              className="z-50 min-w-44 rounded-xl border border-white/10 bg-ink-850 p-1 shadow-xl"
            >
              {!isMine && (
                <DropdownMenu.Item
                  className="flex h-8 cursor-pointer items-center gap-2 rounded-lg px-2 text-sm text-fg-muted outline-none data-[highlighted]:bg-white/[0.07] data-[highlighted]:text-fg"
                  onSelect={() => openDialog({ kind: 'report', target: null, message: m })}
                >
                  <Flag size={14} aria-hidden /> Report message
                </DropdownMenu.Item>
              )}
              {isMod && (
                <DropdownMenu.Item
                  className="flex h-8 cursor-pointer items-center gap-2 rounded-lg px-2 text-sm text-[#f0c9a0] outline-none data-[highlighted]:bg-warn/10"
                  onSelect={() => void del.run({ messageId: m.id })}
                >
                  <Trash size={14} aria-hidden /> Delete for everyone
                </DropdownMenu.Item>
              )}
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      )}
    </li>
  );
});

function Composer() {
  const data = useRoomStore((s) => s.data)!;
  const status = useRoomStore((s) => s.status);
  const session = useSession().data!;
  const loc = useLocation();
  const send = useCommand('chat.send');
  const [body, setBody] = useState('');
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [, force] = useState(0);
  const slow = data.room.settings.slowModeSeconds;
  const mutedUntil = data.me.mutedUntilMs;
  const muted = mutedUntil !== null && mutedUntil > Date.now();
  const cooldownLeft = Math.max(0, Math.ceil((cooldownUntil - Date.now()) / 1000));

  useEffect(() => {
    if (cooldownLeft <= 0 && !muted) return;
    const t = setInterval(() => force((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [cooldownLeft, muted]);

  if (!session.user) {
    return (
      <div className="border-t border-white/[0.06] p-3 text-sm text-fg-muted">
        <Link
          to={`/signin?returnTo=${encodeURIComponent(loc.pathname)}`}
          className="font-medium text-accent"
        >
          Sign in
        </Link>{' '}
        to chat. Guests can listen.
      </div>
    );
  }

  const submit = async () => {
    const text = body.trim();
    if (!text || send.pending) return;
    const ack = await send.run({ body: text });
    if (ack.ok) {
      setBody('');
      if (slow > 0) setCooldownUntil(Date.now() + slow * 1000);
    } else if (ack.error.code === 'SLOW_MODE' && ack.error.retryAfterMs) {
      setCooldownUntil(Date.now() + ack.error.retryAfterMs);
    }
  };

  const disabled = muted || status !== 'live';
  return (
    <form
      className="border-t border-white/[0.06] p-3"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      {muted && (
        <p className="mb-2 text-xs text-warn">
          A moderator muted you until{' '}
          {new Date(mutedUntil!).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}.
        </p>
      )}
      <div className="flex items-end gap-2">
        <label className="sr-only" htmlFor="chat-input">
          Message the room
        </label>
        <textarea
          id="chat-input"
          rows={1}
          value={body}
          maxLength={LIMITS.chatBodyMax}
          disabled={disabled}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void submit();
            }
          }}
          placeholder={
            muted ? 'You are muted' : status !== 'live' ? 'Reconnecting…' : 'Message the room'
          }
          className="max-h-28 min-h-10 flex-1 resize-none rounded-xl border border-white/10 bg-ink-900 px-3 py-2.5 text-sm placeholder:text-fg-subtle focus:border-accent/60 focus:outline-none focus:ring-2 focus:ring-accent/25 disabled:opacity-60"
        />
        <Button
          type="submit"
          variant="primary"
          disabled={disabled || !body.trim() || cooldownLeft > 0}
          loading={send.pending}
          aria-label="Send message"
          className="h-10 w-10 px-0"
        >
          {cooldownLeft > 0 ? (
            <span className="font-mono text-xs">{cooldownLeft}</span>
          ) : (
            <PaperPlaneRight size={16} weight="fill" aria-hidden />
          )}
        </Button>
      </div>
      <div className="mt-1.5 flex justify-between text-[11px] text-fg-subtle">
        <span>
          {slow > 0
            ? `Slow mode: one message every ${slow}s`
            : 'Enter to send · Shift+Enter for a new line'}
        </span>
        {body.length > LIMITS.chatBodyMax - 80 && (
          <span className="font-mono">
            {body.length}/{LIMITS.chatBodyMax}
          </span>
        )}
      </div>
    </form>
  );
}
